import React, { useState, useEffect, useCallback } from 'react';
import { FaFileContract, FaFileAlt, FaDownload, FaEye, FaPaperPlane, FaCheckCircle, FaClock, FaExclamationTriangle, FaRedo, FaUpload, FaInfoCircle, FaTrash, FaLayerGroup, FaFilePdf } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  DocumentInstance,
  fetchDocuments,
  downloadDocument,
  sendForESign,
  getESignStatus,
  uploadSigned,
  regenerateDocument,
  deleteDocument,
  uploadDocument,
} from '../services/DocumentService';
import { ContractWizardModal } from '../../contracts/components/ContractWizardModal';
import { useCompensationUnlock } from '../hooks/useCompensationUnlock';
import { UploadSignedModal } from './UploadSignedModal';
import { DocumentDetailDrawer } from './DocumentDetailDrawer';
import { absolutizePdfUrl } from '../../../utils/pdfUtils';
import { personalakteStyles as ps, colors, shadows } from '../styles/personalakteStyles';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleString('de-DE');
};

interface EmployeeDocumentsTabProps {
  employeeId: number;
  employeeData?: {
    employment_type?: string;
    primary_work_area?: { id: number; name: string };
    work_areas?: Array<{ id: number; name: string }>;
    person?: { first_name?: string; last_name?: string; email?: string };
    start_date?: string;
    position_key?: string;  // ← NEU: Position Key für Contract Wizard
  };
}

type DocCategory = 'all' | 'contract' | 'payslip' | 'other';

const CATEGORY_LABELS: Record<DocCategory, string> = {
  all: 'Alle',
  contract: 'Vertraege',
  payslip: 'Lohnabrechnungen',
  other: 'Sonstiges',
};

function getDocCategory(doc: DocumentInstance): 'contract' | 'payslip' | 'other' {
  if (doc.template_name || doc.template_key) return 'contract';
  if (doc.contains_compensation) return 'payslip';
  return 'other';
}

const EmployeeDocumentsTab: React.FC<EmployeeDocumentsTabProps> = ({ employeeId, employeeData }) => {
  const [documents, setDocuments] = useState<DocumentInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; traceId?: string } | null>(null);
  const [showWizard, setShowWizard] = useState(false);
  const [actionLoading, setActionLoading] = useState<{ [key: number]: string }>({});
  const [categoryFilter, setCategoryFilter] = useState<DocCategory>('all');
  const [uploadModalDoc, setUploadModalDoc] = useState<DocumentInstance | null>(null);
  const [detailDrawerDoc, setDetailDrawerDoc] = useState<DocumentInstance | null>(null);
  const [deleteConfirmDoc, setDeleteConfirmDoc] = useState<DocumentInstance | null>(null);
  const [selectedDocIds, setSelectedDocIds] = useState<Set<number>>(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadCompensation, setUploadCompensation] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { ensureUnlocked, showPinDialog, pinError, handlePinSubmit, cancelUnlock } = useCompensationUnlock();

  const handleApiError = (err: any, defaultMessage: string) => {
    const axiosError = err?.originalError || err;
    const response = axiosError?.response;
    const data = response?.data || err?.data;
    const traceId = response?.headers?.['x-trace-id'];
    const status = response?.status;

    let errorMessage = data?.message || data?.detail || defaultMessage;

    if (status === 422) {
      errorMessage = 'Dokument ist noch nicht gerendert – bitte \'Regenerate\' ausführen';
    } else if (status === 403) {
      errorMessage = 'Zugriff verweigert – bitte PIN eingeben oder Berechtigung prüfen';
    } else if (status === 404) {
      errorMessage = 'Dokument nicht gefunden – möglicherweise wurde es gelöscht';
    }

    toast.error(
      <div>
        <div>{errorMessage}</div>
        {traceId && <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Trace ID: {traceId}</div>}
      </div>
    );
  };

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const docs = await fetchDocuments(employeeId);
      setDocuments(docs);
    } catch (err: any) {
      const axiosError = err?.originalError || err;
      const response = axiosError?.response;
      const data = response?.data || err?.data;
      const traceId = response?.headers?.['x-trace-id'];

      setError({
        message: data?.message || data?.detail || 'Failed to load documents',
        traceId,
      });
      console.error('Failed to load documents:', err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  const handleDownload = async (doc: DocumentInstance) => {
    if (doc.contains_compensation) {
      const unlocked = await ensureUnlocked();
      if (!unlocked) return;
    }

    setActionLoading((prev) => ({ ...prev, [doc.id]: 'downloading' }));
    try {
      {
        const blob = await downloadDocument(doc.id);
        const blobUrl = window.URL.createObjectURL(blob);
        const filename = doc.position_title || `document_${doc.id}`;
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${filename}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
        toast.success('Document downloaded successfully');
      }
    } catch (err: any) {
      handleApiError(err, 'Failed to download document');
      console.error('Download failed:', err);
    } finally {
      setActionLoading((prev) => {
        const updated = { ...prev };
        delete updated[doc.id];
        return updated;
      });
    }
  };

  const handlePreview = async (doc: DocumentInstance) => {
    if (doc.contains_compensation) {
      const unlocked = await ensureUnlocked();
      if (!unlocked) return;
    }

    setActionLoading((prev) => ({ ...prev, [doc.id]: 'previewing' }));
    try {
      const url = doc.current_version?.preview_url || doc.current_version?.download_url || doc.pdf_url;
      if (url) {
        window.open(absolutizePdfUrl(url), '_blank', 'noopener,noreferrer');
      } else {
        const blob = await downloadDocument(doc.id);
        const blobUrl = window.URL.createObjectURL(blob);
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
        setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
      }
    } catch (err: any) {
      handleApiError(err, 'Failed to preview document');
      console.error('Preview failed:', err);
    } finally {
      setActionLoading((prev) => {
        const updated = { ...prev };
        delete updated[doc.id];
        return updated;
      });
    }
  };

  const handleSendForESign = async (doc: DocumentInstance) => {
    setActionLoading((prev) => ({ ...prev, [doc.id]: 'sending' }));
    try {
      const payload = {
        recipient_email: employeeData?.person?.email,
        recipient_name: `${employeeData?.person?.first_name || ''} ${employeeData?.person?.last_name || ''}`.trim(),
      };
      await sendForESign(employeeId, doc.id, payload);
      toast.success('Document sent for eSign successfully');
      await loadDocuments();
    } catch (err: any) {
      if (err.response?.status === 404) {
        toast.info('eSign-Funktion ist noch nicht verfügbar');
      } else {
        handleApiError(err, 'Failed to send document for eSign');
      }
      console.error('Send for eSign failed:', err);
    } finally {
      setActionLoading((prev) => {
        const updated = { ...prev };
        delete updated[doc.id];
        return updated;
      });
    }
  };

  const handleViewESignStatus = async (doc: DocumentInstance) => {
    setActionLoading((prev) => ({ ...prev, [doc.id]: 'checking' }));
    try {
      const status = await getESignStatus(employeeId, doc.id);
      toast.info(
        <div>
          <div><strong>eSign Status:</strong> {status.status}</div>
          {status.sent_at && <div>Sent: {formatDate(status.sent_at)}</div>}
          {status.signed_at && <div>Signed: {formatDate(status.signed_at)}</div>}
        </div>,
        { autoClose: 5000 }
      );
      await loadDocuments();
    } catch (err: any) {
      if (err.response?.status === 404) {
        toast.info('eSign-Status ist noch nicht verfügbar');
      } else {
        handleApiError(err, 'Failed to check eSign status');
      }
      console.error('eSign status check failed:', err);
    } finally {
      setActionLoading((prev) => {
        const updated = { ...prev };
        delete updated[doc.id];
        return updated;
      });
    }
  };

  const handleUploadSignedSubmit = async (file: File, signedAt: string) => {
    if (!uploadModalDoc) return;

    try {
      await uploadSigned(employeeId, uploadModalDoc.id, file, signedAt);
      toast.success('Signed document uploaded successfully');
      await loadDocuments();
      setUploadModalDoc(null);
    } catch (err: any) {
      handleApiError(err, 'Failed to upload signed document');
      throw err;
    }
  };

  const handleRegenerate = async (doc: DocumentInstance) => {
    if (doc.contains_compensation) {
      const unlocked = await ensureUnlocked();
      if (!unlocked) return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to regenerate this document?\n\nTemplate: ${doc.template_name || doc.template_key || (doc.template_id ? `#${doc.template_id}` : 'Unbekannt')}\n\nThis will create a new version.`
    );
    if (!confirmed) return;

    setActionLoading((prev) => ({ ...prev, [doc.id]: 'regenerating' }));
    try {
      const updatedDoc = await regenerateDocument(employeeId, doc.id, {});
      toast.success(
        <div>
          <div>Neue Version erstellt (#v{updatedDoc.version})</div>
          <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Document ID: {updatedDoc.id}</div>
        </div>
      );
      await loadDocuments();
    } catch (err: any) {
      handleApiError(err, 'Failed to regenerate document');
      console.error('Regenerate failed:', err);
    } finally {
      setActionLoading((prev) => {
        const updated = { ...prev };
        delete updated[doc.id];
        return updated;
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmDoc) return;

    setActionLoading((prev) => ({ ...prev, [deleteConfirmDoc.id]: 'deleting' }));
    try {
      await deleteDocument(employeeId, deleteConfirmDoc.id);
      toast.success('Vertrag erfolgreich gelöscht');
      await loadDocuments();
      setDeleteConfirmDoc(null);
    } catch (err: any) {
      handleApiError(err, 'Failed to delete document');
      console.error('Delete failed:', err);
    } finally {
      setActionLoading((prev) => {
        const updated = { ...prev };
        if (deleteConfirmDoc) {
          delete updated[deleteConfirmDoc.id];
        }
        return updated;
      });
    }
  };

  const toggleDocSelection = (docId: number) => {
    setSelectedDocIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(docId)) {
        newSet.delete(docId);
      } else {
        newSet.add(docId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDocIds.size === documents.length) {
      setSelectedDocIds(new Set());
    } else {
      setSelectedDocIds(new Set(documents.map((doc) => doc.id)));
    }
  };

  const handleBulkDeleteConfirm = async () => {
    const idsToDelete = Array.from(selectedDocIds);
    if (idsToDelete.length === 0) return;

    let successCount = 0;
    let failCount = 0;

    for (const docId of idsToDelete) {
      setActionLoading((prev) => ({ ...prev, [docId]: 'deleting' }));
      try {
        await deleteDocument(employeeId, docId);
        successCount++;
      } catch (err: any) {
        failCount++;
        console.error(`Failed to delete document ${docId}:`, err);
      } finally {
        setActionLoading((prev) => {
          const updated = { ...prev };
          delete updated[docId];
          return updated;
        });
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} Vertrag${successCount > 1 ? 'e' : ''} erfolgreich gelöscht`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} Vertrag${failCount > 1 ? 'e' : ''} konnten nicht gelöscht werden`);
    }

    await loadDocuments();
    setSelectedDocIds(new Set());
    setShowBulkDeleteConfirm(false);
  };

  const handleUploadDocument = async () => {
    if (uploadFiles.length === 0) return;
    setUploading(true);
    let successCount = 0;
    let failCount = 0;
    for (const file of uploadFiles) {
      const title = file.name.replace('.pdf', '').replace('.PDF', '');
      try {
        await uploadDocument(employeeId, file, title, uploadCompensation);
        successCount++;
      } catch (err: any) {
        failCount++;
        console.error(`Upload failed for ${file.name}:`, err);
      }
    }
    if (successCount > 0) toast.success(`${successCount} Dokument${successCount > 1 ? 'e' : ''} hochgeladen`);
    if (failCount > 0) toast.error(`${failCount} Upload${failCount > 1 ? 's' : ''} fehlgeschlagen`);
    await loadDocuments();
    setShowUploadModal(false);
    setUploadFiles([]);
    setUploadCompensation(false);
    setUploading(false);
  };

  const getStatusChip = (status: DocumentInstance['status']) => {
    const styles: Record<string, { bg: string; color: string; icon: React.ReactElement }> = {
      DRAFT: { bg: colors.borderLight, color: colors.textMuted, icon: <FaClock size={12} /> },
      READY: { bg: colors.infoLight, color: colors.info, icon: <FaCheckCircle size={12} /> },
      SENT: { bg: colors.warningLight, color: colors.warning, icon: <FaPaperPlane size={12} /> },
      SIGNED: { bg: colors.successLight, color: colors.success, icon: <FaCheckCircle size={12} /> },
      EXPIRED: { bg: colors.dangerLight, color: colors.danger, icon: <FaExclamationTriangle size={12} /> },
      ERROR: { bg: colors.dangerLight, color: colors.danger, icon: <FaExclamationTriangle size={12} /> },
    };

    const style = styles[status] || styles.DRAFT;

    return (
      <span style={ps.badge(style.bg, style.color)}>
        {style.icon}
        {status}
      </span>
    );
  };

  const workAreas = employeeData?.work_areas || (employeeData?.primary_work_area ? [employeeData.primary_work_area] : []);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: colors.navy, margin: 0 }}>
          Documents
          {selectedDocIds.size > 0 && (
            <span style={{ marginLeft: 12, fontSize: 14, color: colors.textMuted, fontWeight: 400 }}>
              ({selectedDocIds.size} ausgewählt)
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 12 }}>
          {selectedDocIds.size > 0 && (
            <button
              className="hover-btn-primary"
              onClick={() => setShowBulkDeleteConfirm(true)}
              style={{
                ...ps.buttonPrimary,
                background: colors.danger,
                boxShadow: '0 2px 8px rgba(239, 68, 68, 0.3)',
              }}
            >
              <FaTrash size={14} />
              {selectedDocIds.size} Vertrag{selectedDocIds.size > 1 ? 'e' : ''} löschen
            </button>
          )}
          <button
            onClick={() => setShowUploadModal(true)}
            title="Upload PDF"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              border: `1px solid ${colors.border}`,
              background: colors.bgMuted,
              color: colors.textMuted,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s',
              fontSize: 16,
            }}
          >
            <FaUpload size={16} />
          </button>
          <button
            data-testid="create-contract-button"
            className="hover-btn-primary"
            onClick={() => setShowWizard(true)}
            style={ps.buttonPrimary}
          >
            Create Document
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            background: colors.dangerLight,
            border: `1px solid ${colors.danger}`,
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <div style={{ color: colors.danger, fontWeight: 600, marginBottom: 4 }}>
              <FaExclamationTriangle style={{ marginRight: 8 }} />
              {error.message}
            </div>
            {error.traceId && (
              <div style={{ fontSize: 12, color: colors.textMuted }}>
                Trace ID: {error.traceId}
              </div>
            )}
          </div>
          <button
            onClick={loadDocuments}
            style={{
              ...ps.buttonPrimary,
              background: colors.danger,
              boxShadow: 'none',
              padding: '8px 16px',
              fontSize: 13,
            }}
          >
            <FaRedo size={12} />
            Retry
          </button>
        </div>
      )}

      {documents.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {(Object.keys(CATEGORY_LABELS) as DocCategory[]).map((cat) => {
            const count = cat === 'all' ? documents.length : documents.filter(d => getDocCategory(d) === cat).length;
            if (cat !== 'all' && count === 0) return null;
            const isActive = categoryFilter === cat;
            return (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                style={{
                  padding: '6px 14px',
                  borderRadius: 20,
                  border: isActive ? '1px solid rgba(14, 116, 144, 0.3)' : '1px solid rgba(0,0,0,0.1)',
                  background: isActive ? 'rgba(14, 116, 144, 0.12)' : 'transparent',
                  color: isActive ? '#0e7490' : colors.textMuted,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {CATEGORY_LABELS[cat]} ({count})
              </button>
            );
          })}
        </div>
      )}

      <div
        style={{
          ...ps.card,
          padding: 0,
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: colors.textLight }}>
            <div>Loading documents...</div>
          </div>
        ) : documents.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <FaFileAlt size={56} color={colors.borderLight} />
            <h3 style={{ marginTop: 16, fontSize: 18, fontWeight: 600, color: colors.navy }}>
              No documents yet
            </h3>
            <p style={{ fontSize: 14, marginTop: 8, color: colors.textMuted, maxWidth: 500, margin: '8px auto 0' }}>
              Click "Create Document" above to generate a contract using the wizard.
            </p>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{
                background: colors.bgMuted,
                borderBottom: `1px solid ${colors.border}`,
              }}>
                <th style={{ padding: 16, width: 40 }}>
                  <input
                    type="checkbox"
                    checked={documents.length > 0 && selectedDocIds.size === documents.length}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer', width: 16, height: 16 }}
                  />
                </th>
                <th style={{ padding: 16, textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 13 }}>
                  Position
                </th>
                <th style={{ padding: 16, textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 13 }}>
                  Status
                </th>
                <th style={{ padding: 16, textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 13 }}>
                  Last Rendered
                </th>
                <th style={{ padding: 16, textAlign: 'right', fontWeight: 600, color: colors.textMuted, fontSize: 13 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {documents.filter(doc => categoryFilter === 'all' || getDocCategory(doc) === categoryFilter).map((doc) => (
                <tr key={doc.id} style={{ borderBottom: `1px solid ${colors.borderLight}`, backgroundColor: selectedDocIds.has(doc.id) ? colors.infoLight : 'transparent' }}>
                  <td style={{ padding: 16 }}>
                    <input
                      type="checkbox"
                      checked={selectedDocIds.has(doc.id)}
                      onChange={() => toggleDocSelection(doc.id)}
                      style={{ cursor: 'pointer', width: 16, height: 16 }}
                    />
                  </td>
                  <td style={{ padding: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      <div
                        style={{ fontWeight: 600, color: colors.info, cursor: 'pointer' }}
                        onClick={() => setDetailDrawerDoc(doc)}
                        title="Vorschau oeffnen"
                      >
                        {doc.position_title || doc.template_name || 'Position unbekannt'}
                      </div>
                      {doc.bundle && (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                          <span style={ps.badge(colors.successLight, colors.success)}>
                            <FaLayerGroup size={11} />
                            Bundle
                          </span>
                          {doc.bundle.items && (
                            <span style={ps.badge(colors.infoLight, colors.info)}>
                              Anhänge: {doc.bundle.items.length}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: 16 }}>
                    {getStatusChip(doc.status)}
                    {doc.status === 'ERROR' && doc.error_message && (
                      <div style={{ fontSize: 12, color: colors.danger, marginTop: 4 }}>
                        {doc.error_message}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: 16, fontSize: 14, color: colors.textMuted }}>
                    {doc.current_version?.rendered_at ? formatDate(doc.current_version.rendered_at) : '—'}
                  </td>
                  <td style={{ padding: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, alignItems: 'center' }}>
                      {doc.bundle?.combined_pdf_url && (
                        <button
                          className="hover-btn-secondary"
                          onClick={() => {
                            const url = absolutizePdfUrl(doc.bundle!.combined_pdf_url!);
                            window.open(url, '_blank', 'noopener,noreferrer');
                          }}
                          disabled={!!actionLoading[doc.id]}
                          style={{
                            ...ps.actionButton,
                            background: colors.successLight,
                            color: colors.success,
                            borderColor: colors.success,
                            cursor: actionLoading[doc.id] ? 'wait' : 'pointer',
                          }}
                          title="Open Combined PDF"
                        >
                          <FaFilePdf size={14} />
                          Combined PDF
                        </button>
                      )}

                      <button
                        className="hover-btn-secondary"
                        onClick={() => setDetailDrawerDoc(doc)}
                        disabled={!!actionLoading[doc.id]}
                        style={{
                          ...ps.actionButton,
                          background: colors.infoLight,
                          color: colors.info,
                          borderColor: colors.info,
                          cursor: actionLoading[doc.id] ? 'wait' : 'pointer',
                        }}
                        title="View Details"
                      >
                        <FaInfoCircle size={14} />
                        Details
                      </button>

                      {doc.status === 'READY' && getDocCategory(doc) === 'contract' && (
                        <button
                          className="hover-btn-secondary"
                          onClick={() => handleSendForESign(doc)}
                          disabled={!!actionLoading[doc.id]}
                          style={{
                            ...ps.actionButton,
                            background: '#f3e5f5',
                            color: '#7b1fa2',
                            borderColor: '#7b1fa2',
                            cursor: actionLoading[doc.id] ? 'wait' : 'pointer',
                          }}
                          title="Send for eSign"
                        >
                          <FaPaperPlane size={14} />
                          {actionLoading[doc.id] === 'sending' ? 'Sending...' : 'Send eSign'}
                        </button>
                      )}

                      {getDocCategory(doc) !== 'contract' && (
                        <button
                          className="hover-btn-secondary"
                          onClick={() => handleDownload(doc)}
                          disabled={!!actionLoading[doc.id]}
                          style={{
                            ...ps.actionButton,
                            background: 'rgba(16, 185, 129, 0.1)',
                            color: '#059669',
                            borderColor: '#059669',
                            cursor: actionLoading[doc.id] ? 'wait' : 'pointer',
                          }}
                          title="Download"
                        >
                          <FaDownload size={14} />
                          {actionLoading[doc.id] === 'downloading' ? 'Laden...' : 'Download'}
                        </button>
                      )}

                      <button
                        onClick={() => setDeleteConfirmDoc(doc)}
                        disabled={!!actionLoading[doc.id]}
                        style={{
                          padding: 8,
                          background: 'transparent',
                          color: colors.danger,
                          border: 'none',
                          borderRadius: 8,
                          cursor: actionLoading[doc.id] ? 'wait' : 'pointer',
                          fontSize: 16,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                        title="Delete Document"
                      >
                        <FaTrash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showWizard && (
        <ContractWizardModal
          open={showWizard}
          onClose={() => setShowWizard(false)}
          employeeId={employeeId}
          workAreas={workAreas}
          currentEmploymentType={employeeData?.employment_type}
          currentWorkAreaId={employeeData?.primary_work_area?.id}
          currentPositionKey={employeeData?.position_key}
          currentStartDate={employeeData?.start_date}
          asDrawer={true}
          onSuccess={(data) => {
            toast.success(
              <div>
                <div>Contract created successfully!</div>
                <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Document ID: {data.document_id}</div>
                {data.trace_id && (
                  <div style={{ fontSize: '0.85em', marginTop: '4px' }}>Trace ID: {data.trace_id}</div>
                )}
              </div>
            );
            loadDocuments();
          }}
        />
      )}

      {showPinDialog && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={cancelUnlock}
        >
          <div
            style={{
              ...ps.card,
              padding: 24,
              maxWidth: 400,
              width: '90%',
              boxShadow: shadows.elevated,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 16px 0', color: colors.navy, fontSize: 18, fontWeight: 600 }}>
              Enter Compensation PIN
            </h3>
            <p style={{ marginBottom: 16, color: colors.textMuted, fontSize: 14 }}>
              This document contains compensation data and requires PIN verification.
            </p>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const pin = formData.get('pin') as string;
                await handlePinSubmit(pin);
              }}
            >
              <div>
                <label style={ps.labelStyle}>PIN</label>
                <input
                  type="password"
                  name="pin"
                  autoFocus
                  style={{
                    ...ps.inputStyle,
                    borderColor: pinError ? colors.danger : colors.border,
                  }}
                />
                {pinError && (
                  <div style={{ marginTop: 4, color: colors.danger, fontSize: 12 }}>
                    {pinError}
                  </div>
                )}
              </div>

              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
                <button
                  type="button"
                  className="hover-btn-secondary"
                  onClick={cancelUnlock}
                  style={ps.buttonSecondary}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="hover-btn-primary"
                  style={ps.buttonPrimary}
                >
                  Unlock
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <UploadSignedModal
        isOpen={uploadModalDoc !== null}
        onClose={() => setUploadModalDoc(null)}
        onSuccess={() => {
          toast.success('Signed document uploaded successfully');
          loadDocuments();
          setUploadModalDoc(null);
        }}
        documentName={uploadModalDoc?.template_name || uploadModalDoc?.template_key || (uploadModalDoc?.template_id ? `#${uploadModalDoc.template_id}` : 'Unbekannt')}
        employeeId={employeeId}
        documentId={uploadModalDoc?.id || 0}
      />

      <DocumentDetailDrawer
        isOpen={detailDrawerDoc !== null}
        onClose={() => setDetailDrawerDoc(null)}
        document={detailDrawerDoc}
        employeeId={employeeId}
        onRegenerate={handleRegenerate}
        onUploadSigned={setUploadModalDoc}
        onDownload={handleDownload}
      />

      {deleteConfirmDoc && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setDeleteConfirmDoc(null)}
        >
          <div
            style={{
              ...ps.card,
              padding: 32,
              maxWidth: 500,
              width: '90%',
              boxShadow: shadows.elevated,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
              <div style={{ color: colors.danger, fontSize: 24 }}>
                <FaExclamationTriangle />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 12px 0', color: colors.navy, fontSize: 20, fontWeight: 600 }}>
                  Vertrag löschen?
                </h3>
                <p style={{ margin: 0, color: colors.textMuted, fontSize: 14, lineHeight: 1.5 }}>
                  Willst Du wirklich den Vertrag löschen?
                </p>
                <div style={ps.innerContainer}>
                  <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4 }}>
                    <strong>Position:</strong> {deleteConfirmDoc.position_title || deleteConfirmDoc.template_name || 'Position unbekannt'}
                  </div>
                  <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 4 }}>
                    <strong>Dokument ID:</strong> #{deleteConfirmDoc.id}
                  </div>
                  <div style={{ fontSize: 13, color: colors.textMuted }}>
                    <strong>Version:</strong> v{deleteConfirmDoc.version}
                  </div>
                </div>
                <div style={{ marginTop: 12, padding: 12, background: colors.dangerLight, borderRadius: 8, border: `1px solid ${colors.danger}` }}>
                  <div style={{ fontSize: 13, color: colors.danger, fontWeight: 500 }}>
                    Diese Aktion kann nicht rückgängig gemacht werden!
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                type="button"
                className="hover-btn-secondary"
                onClick={() => setDeleteConfirmDoc(null)}
                disabled={!!actionLoading[deleteConfirmDoc.id]}
                style={{
                  ...ps.buttonSecondary,
                  cursor: actionLoading[deleteConfirmDoc.id] ? 'wait' : 'pointer',
                }}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="hover-btn-primary"
                onClick={handleDeleteConfirm}
                disabled={!!actionLoading[deleteConfirmDoc.id]}
                style={{
                  ...ps.buttonPrimary,
                  background: colors.danger,
                  boxShadow: 'none',
                  cursor: actionLoading[deleteConfirmDoc.id] ? 'wait' : 'pointer',
                }}
              >
                <FaTrash size={14} />
                {actionLoading[deleteConfirmDoc.id] === 'deleting' ? 'Wird gelöscht...' : 'Ja, löschen'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => { setShowUploadModal(false); setUploadFiles([]); }}
        >
          <div
            style={{ ...ps.card, padding: 32, maxWidth: 500, width: '90%', boxShadow: shadows.elevated }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px 0', color: colors.navy, fontSize: 18, fontWeight: 600 }}>
              Dokumente hochladen
            </h3>

            <div style={{ marginBottom: 16 }}>
              <label style={ps.labelStyle}>PDF-Dateien (mehrere moeglich)</label>
              <div
                style={{
                  padding: 20, border: '2px dashed rgba(0,0,0,0.12)', borderRadius: 10,
                  background: 'rgba(0,0,0,0.02)', textAlign: 'center', cursor: 'pointer',
                }}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'file';
                  input.accept = '.pdf';
                  input.multiple = true;
                  input.onchange = (e) => {
                    const files = Array.from((e.target as HTMLInputElement).files || []);
                    if (files.length > 0) setUploadFiles(prev => [...prev, ...files]);
                  };
                  input.click();
                }}
              >
                {uploadFiles.length > 0 ? (
                  <div style={{ color: colors.info, fontWeight: 600, fontSize: 13 }}>
                    {uploadFiles.length} Datei{uploadFiles.length > 1 ? 'en' : ''} ausgewaehlt
                  </div>
                ) : (
                  <div style={{ color: colors.textMuted, fontSize: 13 }}>
                    Klicken um PDFs auszuwaehlen
                  </div>
                )}
              </div>
              {uploadFiles.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 150, overflowY: 'auto' }}>
                  {uploadFiles.map((f, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 12, color: colors.slate }}>
                      <span>{f.name} ({(f.size / 1024).toFixed(0)} KB)</span>
                      <button
                        onClick={() => setUploadFiles(prev => prev.filter((_, idx) => idx !== i))}
                        style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
                      >
                        x
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: colors.slate, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={uploadCompensation}
                  onChange={(e) => setUploadCompensation(e.target.checked)}
                  style={{ accentColor: colors.info }}
                />
                Enthaelt Gehaltsinformationen (PIN-geschuetzt)
              </label>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                className="hover-btn-secondary"
                onClick={() => { setShowUploadModal(false); setUploadFiles([]); }}
                style={ps.buttonSecondary}
              >
                Abbrechen
              </button>
              <button
                className="hover-btn-primary"
                onClick={handleUploadDocument}
                disabled={uploadFiles.length === 0 || uploading}
                style={{
                  ...ps.buttonPrimary,
                  background: uploadFiles.length === 0 || uploading ? 'rgba(0,0,0,0.1)' : undefined,
                  cursor: uploadFiles.length === 0 || uploading ? 'not-allowed' : 'pointer',
                }}
              >
                <FaUpload size={14} />
                {uploading ? `Hochladen (${uploadFiles.length})...` : `${uploadFiles.length} Datei${uploadFiles.length !== 1 ? 'en' : ''} hochladen`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBulkDeleteConfirm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setShowBulkDeleteConfirm(false)}
        >
          <div
            style={{
              ...ps.card,
              padding: 32,
              maxWidth: 600,
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: shadows.elevated,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 24 }}>
              <div style={{ color: colors.danger, fontSize: 24 }}>
                <FaExclamationTriangle />
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ margin: '0 0 12px 0', color: colors.navy, fontSize: 20, fontWeight: 600 }}>
                  {selectedDocIds.size} Vertrag{selectedDocIds.size > 1 ? 'e' : ''} löschen?
                </h3>
                <p style={{ margin: 0, color: colors.textMuted, fontSize: 14, lineHeight: 1.5 }}>
                  Willst Du wirklich {selectedDocIds.size} Vertrag{selectedDocIds.size > 1 ? 'e' : ''} löschen?
                </p>
                <div style={{ ...ps.innerContainer, marginTop: 16, maxHeight: 300, overflow: 'auto' }}>
                  <div style={{ fontSize: 13, color: colors.textMuted, marginBottom: 8, fontWeight: 600 }}>
                    Ausgewählte Dokumente:
                  </div>
                  {documents
                    .filter((doc) => selectedDocIds.has(doc.id))
                    .map((doc) => (
                      <div key={doc.id} style={{ fontSize: 13, color: colors.textMuted, marginBottom: 6, paddingBottom: 6, borderBottom: `1px solid ${colors.borderLight}` }}>
                        <div><strong>Position:</strong> {doc.position_title || doc.template_name || 'Position unbekannt'}</div>
                        <div><strong>ID:</strong> #{doc.id} v{doc.version}</div>
                      </div>
                    ))}
                </div>
                <div style={{ marginTop: 12, padding: 12, background: colors.dangerLight, borderRadius: 8, border: `1px solid ${colors.danger}` }}>
                  <div style={{ fontSize: 13, color: colors.danger, fontWeight: 500 }}>
                    Diese Aktion kann nicht rückgängig gemacht werden!
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                type="button"
                className="hover-btn-secondary"
                onClick={() => setShowBulkDeleteConfirm(false)}
                style={ps.buttonSecondary}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="hover-btn-primary"
                onClick={handleBulkDeleteConfirm}
                style={{
                  ...ps.buttonPrimary,
                  background: colors.danger,
                  boxShadow: 'none',
                }}
              >
                <FaTrash size={14} />
                Ja, alle löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDocumentsTab;
